import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { bffAuthService } from "../../services/BffAuthService";

import { 
  AppBar, Toolbar, IconButton, Typography, useTheme, useMediaQuery,
  Box, Menu, Avatar, Tooltip, MenuItem, Badge, Chip, Fade
} from "@mui/material";
import { 
  Menu as MenuIcon, 
  ChevronLeft as ChevronLeftIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Language as LanguageIcon
} from "@mui/icons-material";

import { useTranslation } from "react-i18next";

import { useDrawerContext } from "../contexts/drawer-context";
import { useAppStore } from "../../app/zustandStore";
import { useFileStore } from "../../app/stores/fileStore";
import { userServerAddress } from '../../common/MetaSetting';
import { getAvatarUrl } from '../../common/avatarUtils';
import { performLogout } from "../../util/authUtils";
import { ROUTES } from "../constants/routes";

// 导入统一样式定义
import { 
  appBarStyles, 
  toolbarStyles, 
  iconButtonStyles, 
  avatarStyles, 
  typographyStyles, 
  boxStyles 
} from "../../styles/base-components";
import { 
  menuStyles, 
  badgeStyles, 
  menuItemStyles 
} from "../../styles/layout-components";


export const Header = () => {
  const [anchorElFile, setAnchorElFile] = useState<null | HTMLElement>(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState<null | HTMLElement>(null);
  const [notificationCount, setNotificationCount] = useState(3);

  const { currentFile } = useFileStore();
  const { themeMode, toggleTheme } = useAppStore();
  const location = useLocation();
  
  // 调试信息：检查文件数据和头像路径
  console.log('Header组件 - 当前文件数据:', currentFile);
  console.log('Header组件 - 头像路径:', currentFile?.avatarPath);
  console.log('Header组件 - 完整头像URL:', currentFile?.avatarPath ? `${userServerAddress}${currentFile.avatarPath}` : '/default_user.png');

  const { isOpened, toggleIsOpened } = useDrawerContext();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // 根据当前页面路径设置对应的标题
  const getHeaderTitle = () => {
    if (location.pathname === ROUTES.dashboard) {
      return t("titleWiseMemo");
    } else {
      return t("titleWiseMemo");
    }
  };

  const getHeaderSubtitle = () => {
    if (location.pathname === ROUTES.dashboard) {
      return t("file.dashboard.description");
    } else {
      return t('header.welcomeMessage');
    }
  };

  const handleOpenFileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFile(event.currentTarget);
  };

  const handleCloseFileMenu = () => {
    setAnchorElFile(null);
  };

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorElNotifications(null);
    setNotificationCount(0);
  };

  const handleToggleDarkMode = () => {
    toggleTheme();
  };

  const handleLanguageToggle = () => {
    const currentLanguage = i18n.language;
    const newLanguage = currentLanguage.startsWith('zh') ? 'en-US' : 'zh-CN';
    i18n.changeLanguage(newLanguage);
    handleCloseFileMenu();
  };

  const handleLogout = async () => {
    console.log(`${t('file.logout.loggingOut')} ${currentFile?.username}`);
    await bffAuthService.logout();
  };

  return (
    <Box 
      position="fixed" 
      sx={appBarStyles.header}
    >
      <Toolbar sx={toolbarStyles.header}>
        {/* 菜单按钮 */}
        <Fade in={true} timeout={500}>
          <IconButton 
            color="inherit" 
            onClick={() => toggleIsOpened(!isOpened)} 
            sx={iconButtonStyles.menuButton}
          >
            {isOpened ? 
              <ChevronLeftIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} /> : 
              <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
            }
          </IconButton>
        </Fade>
        
        {/* Logo和标题 */}
        <Box sx={boxStyles.headerLogo}>
          <img 
            src="/logo.png" 
            alt="CCDTN" 
            style={{ 
              width: '44px', 
              height: '44px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '0px',
              boxShadow: 'none'
            }} 
          />
          <Box>
            <Typography 
              variant="h6" 
              sx={typographyStyles.headerTitle}
            >
              {getHeaderTitle()}
            </Typography>
            <Typography 
              variant="caption" 
              sx={typographyStyles.headerSubtitle}
            >
              {getHeaderSubtitle()}
            </Typography>
          </Box>
        </Box>
        
        {/* 右侧操作区域 */}
        <Box sx={boxStyles.headerActions}>
          {/* 深色模式切换 */}
          <Fade in={!isMobile} timeout={600}>
            <Tooltip title={themeMode === 'dark' ? t('file.switchToLightMode') : t('file.switchToDarkMode')}>
              <IconButton 
                onClick={handleToggleDarkMode}
                sx={iconButtonStyles.themeToggle}
              >
                {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          </Fade>

          {/* 通知按钮 */}
          <Fade in={!isTablet} timeout={700}>
            <Tooltip title={t('file.notifications')}>
              <IconButton 
                onClick={handleOpenNotifications}
                sx={iconButtonStyles.notification}
              >
                <Badge 
                  badgeContent={notificationCount} 
                  color="error"
                  sx={badgeStyles.root}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Fade>

          
          {/* 文件头像 */}
          <Tooltip title={t("file.fileMenu")}>
            <Avatar
              alt={currentFile?.username}
              onClick={handleOpenFileMenu}
              sx={avatarStyles.header}
              src={getAvatarUrl(currentFile?.avatarPath)}
            >
              {currentFile?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
        </Box>

        {/* 文件菜单 */}
        <Menu
          sx={menuStyles.userMenu}
          id="file-menu"
          anchorEl={anchorElFile}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorElFile)}
          onClose={handleCloseFileMenu}
        >
          <MenuItem onClick={handleLanguageToggle}>
            <LanguageIcon fontSize="small" />
            <Typography variant="body2">
              {i18n.language.startsWith('zh') ? t('header.english') : t('header.chinese')}
            </Typography>
          </MenuItem>
          <MenuItem 
            onClick={handleLogout}
            sx={menuItemStyles.logout}
          >
            <LogoutIcon fontSize="small" />
            <Typography variant="body2">{t("button.logout")}</Typography>
          </MenuItem>
        </Menu>

        {/* 通知菜单 */}
        <Menu
          sx={menuStyles.notificationMenu}
          id="notifications-menu"
          anchorEl={anchorElNotifications}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorElNotifications)}
          onClose={handleCloseNotifications}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('file.notifications')}
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {t('file.noNewNotifications')}
            </Typography>
          </Box>
        </Menu>
      </Toolbar>
    </Box>
  );
};

export default Header;
